import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';

/**
 * @see https://daily-dev-tips.com/posts/react-query-as-a-persistent-state-manager/
 *
 * We are using this hook to share persistent data across pages.
 * Often context providers are used for this purpose, but they don't automatically persist via
 * local storage. The amount of code necessary to set that up is relatively terse and we'd need to
 * remember to wrap consumers in the provider. This hook will simply work everywhere we use
 * react-query's `QueryClientProvider` (currently, that's app-wide, but not in the marketing
 * landing pages).
 */
export function usePersistentData<T>(key: string) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery([key], () => localStorage.getItem(key), {
    initialData: localStorage.getItem(key),
  });

  const { mutateAsync: setData } = useMutation(async (data: T) => localStorage.setItem(key, JSON.stringify(data)), {
    onMutate: (mutatedData: T) => {
      const current = data;
      queryClient.setQueryData([key], JSON.stringify(mutatedData));
      return current;
    },
    onError: (_: unknown, __: unknown, rollback: any) => {
      queryClient.setQueryData([key], rollback);
    },
  });

  return { data, setData, isLoading };
}
